import { Box, Button, Flex, Group, Text, TextInput, Tooltip } from '@mantine/core';
import { IconCircleCheck, IconPlus, IconSearch } from '@tabler/icons';
import { useContext, useEffect, useState } from 'react';
import InitialHeaderRow from './InitialHeaderRow';
import SearchRow from './SearchRow';
import Sort from './sort/Sort';
import FilterBar from './Filters/FilterBar';
import FiltersContext from '../../ideas/filters/context/FiltersContext';
import posthog from 'posthog-js';
import IdeaContext from '../IdeaContext';
import { addOptionsToFilters } from '../../../context/helpers';
import { ProgressIcon20, ProgressIcon40, ProgressIcon60, ProgressIcon80, VerifiedIcon } from '../../../icons/x-symbol-svgrepo-com';
import ValidateModal from './ValidateModal'
import { ideaValidation } from '../../../Api';
import { useAuth0 } from "@auth0/auth0-react";
import GeneratedBar from './GeneratedBar';
import {useKnowledgeData} from "../../../hooks/useKnowledgeData";
import {useIdeaFilters} from "../../../hooks/useIdeaFilters";
import {useFields} from "../../../hooks/useFields";
import { useUserPermissions } from '../../../hooks/useUserPermissions';

interface EvidencesHeaderProps {
  addEvidenceFormScreen: boolean;
  setAddEvidenceFormScreen: React.Dispatch<React.SetStateAction<boolean>>;
  setCardView: React.Dispatch<React.SetStateAction<boolean>>;
  searchEvidence: (query: string) => void;
  activeSearch: boolean;
  searchedEvidence: any[]; // Specify the type of elements in searchedEvidence if possible
  showMatchesResults: boolean;
  setShowMatchesResults: React.Dispatch<React.SetStateAction<boolean>>;
  showRequestsResults: boolean;
  setShowRequestsResults: React.Dispatch<React.SetStateAction<boolean>>;
  showQueryResults: boolean;
  setShowQueryResults: React.Dispatch<React.SetStateAction<boolean>>;
  textItemsResultsLength: number;
  query: string;
  sortDescending: boolean;
  reviewMode: any;
  navToPerspective: any;
  setActiveSearch: any;
  allRequests: any;
  displayedMatches:any;
  setIsValidationDone: (loading: boolean) => void;
  isValidationDone: boolean | null;
}


const EvidencesHeader: React.FC<EvidencesHeaderProps> = ({
  setAddEvidenceFormScreen,
  setCardView,
  searchEvidence,
  activeSearch, 
  searchedEvidence,
  showMatchesResults,
  setShowMatchesResults,
  showRequestsResults,
  setShowRequestsResults,
  showQueryResults,
  setShowQueryResults,
  textItemsResultsLength,
  query,
  reviewMode,
  navToPerspective,
  setActiveSearch,
  allRequests,
  displayedMatches,
  setIsValidationDone,
  isValidationDone
}) => {

  const [validateModalOpened, setValidateModalOpened] = useState(false);
  const [ideaValidationData, setideaValidationData] = useState<any>(null);
  const [validationEvidence, setValidationEvidence] = useState(null);
  const [title, setTitle] = useState<string | boolean>("Improve matches accuracy by providing quick feedback");
  const [text, setText] = useState<string | boolean>("Tag a few evidence to fine tune the AI model");
  const [maturity, setMaturity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [validationCount, setValidationCount] = useState<number>(0);
  const [ProgressIcon, setProgressIcon] = useState<React.FC>(() => ProgressIcon20);

  const {
    idea,quarters, weeks, isGenerated, fetchData
  } = useContext(IdeaContext);

  useEffect(() => {
    if (ideaValidationData?.evidences?.length && allRequests?.length) {
      const evidenceId = ideaValidationData.evidences[0]?.evidenceId;
      const matchedRequest = allRequests.find((req: any) => req._id === evidenceId);
      if(!matchedRequest){
        const errorMessage = "Evidence returned from ML api not found in list of suggested evidences"
        posthog.capture('IdeaValidation', {
          ideaId: idea._id,
          evidenceId: evidenceId,
          accountId: idea.accountId,
          errorMessage: errorMessage
        })
        console.error(errorMessage);
        setValidationEvidence(displayedMatches[0])
      } else {
        setValidationEvidence(matchedRequest);
      }
    } else {
      const errorMessage = "Evidences not returned from ML api"
      posthog.capture('IdeaValidation', {
        errorMessage: errorMessage
      })
      console.error(errorMessage);
      setValidationEvidence(displayedMatches[0])
    }
  }, [ideaValidationData, allRequests]);

  const auth0 = useAuth0();
  const { components, enableIdeaValidation } = useFields();
  const { ideasFiltersConfig, setIdeasFiltersConfig } = useIdeaFilters();
  const { members, owners, tagsOptions } = useKnowledgeData();
  const { canEditIdeas } = useUserPermissions();
  const isReadOnly = isGenerated || !canEditIdeas;
  const {
    activeFilters,
    setActiveFilters,
  } = useContext(FiltersContext);

  useEffect(() => {
    if (quarters.length > 0 || weeks.length > 0) {
      const updatedFilters = addOptionsToFilters(ideasFiltersConfig, { 
        quarters: [...quarters.map(
          (q: string) => (q.toLocaleLowerCase())
        ), 'older'], 
        weeks: weeks.map(
          (w: string) => (w.toLocaleLowerCase())
        ),
        components,
        owners,
        members,
        tagsOptions
      });
      setIdeasFiltersConfig?.(updatedFilters);
    }
  }, [quarters, weeks, isGenerated]);

  useEffect(() => {
    setMaturity(idea?.maturity?.maturityLevel)
    handlebannerText(idea?.validationObject?.validationCount)
    if(idea?.validationObject?.verifyState == 'completed' || idea?.maturity?.maturityLevel == 5){
      setIsValidationDone(true)
    } else if(idea?.validationObject?.verifyState == 'continue') {
      setIsValidationDone(false)
      setValidationCount(idea?.validationObject.validationCount)
    } else {
      setIsValidationDone(false)
      setValidationCount(0)
    }
  }, [idea?.maturity, idea?.validationObject, isGenerated]);
  
  useEffect(() => {
    if (maturity === 5) {
      setIsValidationDone(true);
    }
  }, [maturity]);

  useEffect(() => {
    if(idea){
      ideaValidation(auth0, idea._id)
        .then((res) => {
          if(res.data.status == 'done'){
            setIsValidationDone(true)
          } else {
            setIsValidationDone(false)
          }
          setideaValidationData(res.data);
          setMaturity(res.data.maturityLevel)
          handlebannerText(validationCount)
        })
        .catch((e) => console.log(e))
    }
  }, [idea?._id]);

  const handlebannerText = (validationCount : number | undefined) => {
    switch(validationCount){
    case(5): {
      break;
    }
    case(4): {
      setTitle("Improve matches accuracy by providing quick feedback")
      setText("Tag a few evidence to fine tune the AI model for this Idea")
      setProgressIcon(() => ProgressIcon80);
      break;
    }
    case(3): {
      setTitle("Improve matches accuracy by providing quick feedback")
      setText("Tag a few evidence to fine tune the AI model for this Idea")
      setProgressIcon(() => ProgressIcon60);
      break;
    }
    case(2): {
      setTitle("Improve matches accuracy by providing quick feedback")
      setText("Tag a few evidence to fine tune the AI model for this Idea")
      setProgressIcon(() => ProgressIcon40);
      break;
    }
    default: {
      setTitle("Improve matches accuracy by providing quick feedback")
      setText("Tag a few evidence to fine tune the AI model for this Idea")
      setProgressIcon(() => ProgressIcon20);
      break;
    }  
    }
  }
  

  const handleValidate = () => {
    setLoading(true);
    ideaValidation(auth0, idea._id)
      .then((res) => {
        setideaValidationData(res.data);
        setMaturity(res.data.maturityLevel)
        handlebannerText(validationCount)
        setLoading(false);
      })
      .catch((e) => console.log(e))
    setValidateModalOpened(true)
  }
  const bannerShow : boolean = (idea?.validationObject?.verifyState == 'continue' || displayedMatches?.length >= 5 || validationCount >= 1) == true ? true : false
  const showValidationIndication: boolean = (idea?.validationObject?.verifyState == 'completed' || displayedMatches?.length >= 5 || validationCount >= 1) == true ? true : false
  
  return (
    <Box mt={24}  /* sx={{ borderBottom: "1px solid #D8D8DB" }} */>
      {
        validateModalOpened && (
          <ValidateModal
            ideaId={idea._id}
            maturity={maturity}
            validateModalOpened={validateModalOpened}
            setValidateModalOpened={setValidateModalOpened}
            reviewMode={reviewMode}
            navToPerspective={navToPerspective}
            setActiveSearch={setActiveSearch}
            validationEvidence={validationEvidence}
            loading={loading}
            setLoading={setLoading}
            setideaValidationData={setideaValidationData}
            setMaturity={setMaturity}
            handlebannerText={handlebannerText}
            setValidationCount={setValidationCount}
            validationCount={validationCount}
            setIsValidationDone={setIsValidationDone}
            isValidationDone={isValidationDone}
          />
        )
      }
      {
        isValidationDone == false && enableIdeaValidation && !isGenerated && bannerShow && (
          <Group mb={20} position='apart' p="5px" mx="20px" style={{borderRadius: 4, backgroundColor: "#FF8700"}}>
            <Group spacing={8}>
              {validationCount === 0
                ? <VerifiedIcon/> : 
                <Tooltip label="This Idea can be further optimized to receive the most relevant evidences">
                  <Flex align="center"><ProgressIcon /></Flex>
                </Tooltip>
              }
              <Box fz={"12px"} ml={"5px"}>
                <Text fw={700}>
                  {title}
                </Text>
                <Text>
                  {text}
                </Text>
              </Box>
            </Group>
            <Tooltip label={validationCount === 0 ? "Start validating" : "Continue"}>
              <Button 
                size="xs" 
                fz={"12px"}
                fw={500}
                style={{ color: "#ffffff", backgroundColor: "#000000", height: "32px" }}
                onClick={() => handleValidate()}
              >
                {validationCount === 0
                  ? "Start validating" 
                  : "Continue"}
              </Button>
            </Tooltip>
          </Group>
        )
      }
      {isGenerated && <GeneratedBar/>}
      {/* Actions */}

      <Group mb={12} position='apart' px="20px">
        <Group spacing={8}>
          <TextInput
            styles={{
              wrapper:{
                border: "1px solid #D8D8DB",
                borderRadius: "7px",
                backgroundColor: "#fff"
              },
              input:{
                height:32
              }
            }}
            mr={8}
            variant="unstyled"
            placeholder="Search evidence..."
            icon={<IconSearch size={14} />}
            value={query}
            onChange={(event) => {
              searchEvidence(event.currentTarget.value);
              posthog.capture('search_evidence_in_idea', {
                ideaId: idea._id,
                query: event.currentTarget.value
              })
            }}
          />
          <FilterBar 
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
          />
        </Group>
        <Flex>
          {/* Sort */}
          <Sort />
          {!isReadOnly && <Flex
            w={70}
            align="center"
            px="2px"
            sx={{
              borderRadius: "6px",
              border: "1px solid #5C5CEB",
              cursor: "pointer",
            }}
            bg="#5C5CEB"
            h={32}
            ml="xs"
            onClick={() => {
              setCardView(false);
              setAddEvidenceFormScreen(true);
              posthog.capture('add_evidence_clicked', {
                ideaId: idea._id,
                type: 'manually'
              })
            }}
          >
            <Text ml={4} mt={4} p={0} color="white">
              <IconPlus height={16} />
            </Text>
            <Text color="white" fz="14px" fw={500}>
              Add
            </Text>
          </Flex>}
        </Flex>
      </Group>

      {/* Header and filter by suggested */}
      <>
        {!activeSearch ? (
          <Flex>
            <InitialHeaderRow/>
            {isValidationDone && enableIdeaValidation && showValidationIndication && !isGenerated &&
              <Tooltip label="This Idea is optimized to receive the most relevant evidences">
                <Button 
                  size="xs" 
                  fz={"12px"}
                  fw={500}
                  style={{ color: "#000000", backgroundColor: "#ffffff", height: "32px", cursor:"default" }}
                  p={"5px"}
                  mr={"10px"}
                >
                  <IconCircleCheck fill="#0BAC4D" color="#ffffff" size={18}/>
                  <Text ml={"5px"} fz={"12px"} fw={500}>Validated</Text>
                </Button>
              </Tooltip>
            }
          </Flex>
        ) : (
          
          <SearchRow
            searchEvidence={searchEvidence}
            searchedEvidence={searchedEvidence}
            showMatchesResults={showMatchesResults}
            setShowMatchesResults={setShowMatchesResults}
            showRequestsResults={showRequestsResults}
            setShowRequestsResults={setShowRequestsResults}
            showQueryResults={showQueryResults}
            setShowQueryResults={setShowQueryResults}
            resultsLength={textItemsResultsLength}
          />
  
        )}
      </>
    </Box>
  );
};

export default EvidencesHeader